<template>
  <div class="login-page">
    <!-- Header with Logo -->
    <div class="login-header">
      <div class="header-content">
        <img
          class="login-logo"
          src="../../../assets/images/Healthy-Hip-Hop-Logo.png"
          alt="H3 Admin"
        />
        <!-- <h1 class="login-title">H3 Admin</h1> -->
        <p class="login-subtitle">Sign in to your admin dashboard</p>
      </div>
    </div>

    <!-- Login Form -->
    <div class="login-form-container">
      <b-container>
        <b-row class="justify-content-center">
          <b-col lg="4" md="6" sm="8" xs="12">
            <b-card class="login-card shadow">
              <b-card-body class="p-4">
                <div class="text-center mb-4">
                  <h3 class="form-title">Welcome Back</h3>
                  <p class="form-subtitle">Enter your credentials to continue</p>
                </div>

                <validation-observer v-slot="{handleSubmit}" ref="formValidator">
                  <b-form role="form" @submit.prevent="handleSubmit(onSubmit)">
                    <b-form-group>
                      <label class="form-label">Username / Email</label>
                      <b-form-input
                        id="username"
                        type="text"
                        placeholder="Enter your username or email"
                        autocomplete="off"
                        v-model.trim="$v.form.username.$model"
                        @input="$v.form.username.$touch()"
                        :state="!$v.form.username.$error"
                        class="form-control-lg"
                      />
                      <b-form-invalid-feedback v-if="$v.form.username.$error">
                        Username is required
                      </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group>
                      <label class="form-label">Password</label>
                      <b-form-input
                        id="password"
                        type="password"
                        placeholder="Enter your password"
                        autocomplete="off"
                        v-model.trim="$v.form.password.$model"
                        @input="$v.form.password.$touch()"
                        :state="!$v.form.password.$error"
                        class="form-control-lg"
                      />
                      <b-form-invalid-feedback v-if="$v.form.password.$error">
                        Password is required
                      </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group>
                      <b-form-checkbox v-model="form.rememberMe">
                        Remember me
                      </b-form-checkbox>
                    </b-form-group>

                    <div class="text-center">
                      <b-button
                        type="submit"
                        variant="primary"
                        size="lg"
                        block
                        :disabled="$v.form.$invalid || loading"
                        class="login-button"
                      >
                        <b-spinner small v-if="loading" class="mr-2"></b-spinner>
                        <i class="ni ni-lock-circle-open mr-2" v-if="!loading"></i>
                        {{ loading ? 'Signing In...' : 'Sign In' }}
                      </b-button>
                    </div>
                  </b-form>
                </validation-observer>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { required } from 'vuelidate/lib/validators';

  export default {
    data() {
      return {
        model: {
          email: '',
          password: '',
          rememberMe: false
        },
        form: {
          username: null,
          password: null,
          rememberMe: false,
        },
        loading: false
      };
    },
    components : {
      // Components are not needed for this simplified design
    },
    computed: {
      ...mapGetters({
        authStatus: 'auth/authStatus',
      }),
    },
    watch: {
      authStatus(val) {
        if (val)
          this.loading = (val === 'loading') ? true : false;
      },
    },
    methods: {
      /**
       * Admin Login
       */
      onSubmit() {
        this.loading = true;
        this.$store.dispatch('auth/login', {
          email: this.form.username,
          password: this.form.password,
          rememberMe: this.form.rememberMe,
          isAdmin: true,
        })
        .then(response => {
          this.loading = false;
          // Login successful - redirect will be handled by router guard
        })
        .catch(error => {
          this.loading = false;
          // Handle error, e.g., invalid credentials
          this.$bvToast.toast('Invalid credentials, please try again.', {
            title: 'Login Failed',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          });
        });
      },
    },
    validations: {
      form: {
        username: { required },
        password: { required },
      },
    },
  };
</script>

<style scoped>
/* Main Container */
.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
}

.card-body {
  padding: 0;
}

/* Header Section */
.login-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem 0;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  max-width: 400px;
  margin: 0 auto;
}

.login-logo {
  height: 60px;
  width: auto;
  margin-bottom: 1rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.login-title {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.login-subtitle {
  font-size: 1rem !important;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-weight: 400;
}

/* Form Container */
.login-form-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}

.login-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: none;
  max-width: 400px;
  width: 100%;
}

.form-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #32325d;
  margin: 0 0 0.5rem 0;
}

.form-subtitle {
  font-size: 0.9rem;
  color: #8898aa;
  margin: 0;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #32325d;
  margin-bottom: 0.5rem;
}

.form-control-lg {
  border-radius: 8px;
  border: 2px solid #e9ecef;
  padding: 0.75rem 1rem;
  font-size: 1rem;
}

.form-control-lg:focus {
  border-color: #5e72e4;
  box-shadow: 0 0 0 0.2rem rgba(94, 114, 228, 0.15);
}

.custom-control-label {
  font-size: 0.9rem;
  color: #8898aa;
  cursor: pointer;
}

.login-button {
  background: linear-gradient(135deg, #5e72e4 0%, #4c63d2 100%);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem !important;
  font-weight: 600;
  color: white;
  box-shadow: 0 4px 15px rgba(94, 114, 228, 0.3);
  transition: all 0.3s ease;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(94, 114, 228, 0.4);
}

.login-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .login-header {
    padding: 1.5rem 0;
  }

  .login-logo {
    height: 50px;
  }

  .login-title {
    font-size: 1.8rem;
  }

  .login-form-container {
    padding: 1rem;
  }

  .login-card {
    margin: 0 1rem;
  }
}

@media (max-width: 576px) {
  .login-title {
    font-size: 1.6rem;
  }

  .login-subtitle {
    font-size: 0.9rem;
  }

  .form-title {
    font-size: 1.3rem;
  }

  .login-card .card-body {
    padding: 1.5rem;
  }
}

/* Loading States */
.login-button .spinner-border {
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
}

/* Form Validation */
.invalid-feedback {
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.was-validated .form-control:invalid,
.form-control.is-invalid {
  border-color: #dc3545;
}

.was-validated .form-control:invalid:focus,
.form-control.is-invalid:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.15);
}
</style>